import { useState } from 'react';
import { MapContainer, TileLayer, Polyline, useMapEvents, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

interface Coordinate {
  lat: number;
  lng: number;
}

interface WalkingPathMapProps {
  paths: Coordinate[][];
  onChange: (paths: Coordinate[][]) => void;
  locale: string;
}

// Component to handle map clicks
function MapClickHandler({ 
  selectedPathIndex, 
  paths, 
  onChange 
}: { 
  selectedPathIndex: number | null; 
  paths: Coordinate[][]; 
  onChange: (paths: Coordinate[][]) => void;
}) {
  useMapEvents({
    click(e: { latlng: { lat: number; lng: number } }) {
      if (selectedPathIndex !== null) {
        const newPaths = [...paths];
        newPaths[selectedPathIndex] = [
          ...(newPaths[selectedPathIndex] ?? []),
          { lat: e.latlng.lat, lng: e.latlng.lng }
        ];
        onChange(newPaths);
      }
    },
  });
  return null;
}

export function WalkingPathMap({ paths, onChange, locale }: WalkingPathMapProps) {
  const [selectedPathIndex, setSelectedPathIndex] = useState<number | null>(null);
  const [mapCenter] = useState<[number, number]>([24.7136, 46.6753]); // Riyadh, Saudi Arabia
  const [mapZoom] = useState(12);

  const addNewPath = () => {
    onChange([...paths, []]);
    setSelectedPathIndex(paths.length);
  };

  const removePath = (index: number) => {
    const newPaths = paths.filter((_, i) => i !== index);
    onChange(newPaths);
    setSelectedPathIndex(null);
  };

  const clearPath = (index: number) => {
    const newPaths = [...paths];
    newPaths[index] = [];
    onChange(newPaths);
  };

  return (
    <div className="space-y-4">
      {/* Leaflet Map Container */}
      <div className="relative w-full h-96 rounded-xl overflow-hidden border-2 border-gray-300 dark:border-gray-600">
        <MapContainer
          {...({ center: mapCenter, zoom: mapZoom, style: { height: '100%', width: '100%' }, className: 'z-0' } as any)}
        >
          <TileLayer
            {...({ attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png' } as any)}
          />
          
          <MapClickHandler 
            selectedPathIndex={selectedPathIndex} 
            paths={paths} 
            onChange={onChange} 
          />
          
          {/* Render all paths */}
          {paths.map((path, pathIndex) => (
            <div key={pathIndex}>
              {/* Draw polyline for the path */}
              {path.length > 1 && (
                <Polyline
                  {...({ positions: path.map(p => [p.lat, p.lng]), color: selectedPathIndex === pathIndex ? '#005AFF' : '#94A3B8', weight: selectedPathIndex === pathIndex ? 4 : 3, opacity: selectedPathIndex === pathIndex ? 1 : 0.6 } as any)}
                />
              )}
              
              {/* Draw markers for each point */}
              {path.map((point, pointIndex) => (
                <Marker
                  key={`${pathIndex}-${pointIndex}`}
                  {...({ position: [point.lat, point.lng], opacity: selectedPathIndex === pathIndex ? 1 : 0.5 } as any)}
                />
              ))}
            </div>
          ))}
        </MapContainer>
        
        {/* Active path indicator overlay */}
        {selectedPathIndex !== null && (
          <div className="absolute top-4 left-4 bg-white dark:bg-gray-800 px-3 py-2 rounded-lg shadow-lg z-10 border border-gray-200 dark:border-gray-700">
            <p className="text-sm font-medium text-primary">
              {locale === 'ar' 
                ? `المسار النشط: ${selectedPathIndex + 1}`
                : `Active Path: ${selectedPathIndex + 1}`}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {locale === 'ar' 
                ? 'انقر على الخريطة لإضافة نقاط'
                : 'Click on map to add points'}
            </p>
          </div>
        )}
      </div>

      {/* Path List */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {locale === 'ar' ? 'المسارات المحددة' : 'Selected Paths'}
          </h4>
          <button
            type="button"
            onClick={addNewPath}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <span className="material-icons-outlined text-sm">add</span>
            {locale === 'ar' ? 'إضافة مسار' : 'Add Path'}
          </button>
        </div>

        {paths.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
            {locale === 'ar' ? 'لم يتم إضافة مسارات بعد' : 'No paths added yet'}
          </div>
        ) : (
          <div className="space-y-2">
            {paths.map((path, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg border-2 transition-all ${
                  selectedPathIndex === index
                    ? 'border-primary bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                }`}
              >
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setSelectedPathIndex(index)}
                    className="flex items-center gap-3 flex-1 text-left"
                  >
                    <span className="material-icons-outlined text-gray-600 dark:text-gray-400">
                      route
                    </span>
                    <div>
                      <p className="font-medium text-gray-900">
                        {locale === 'ar' ? `المسار ${index + 1}` : `Path ${index + 1}`}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {path.length === 0
                          ? locale === 'ar' ? 'فارغ' : 'Empty'
                          : locale === 'ar'
                          ? `${path.length} نقاط`
                          : `${path.length} points`}
                      </p>
                    </div>
                  </button>
                  <div className="flex items-center gap-2">
                    {path.length > 0 && (
                      <button
                        type="button"
                        onClick={() => clearPath(index)}
                        className="p-1.5 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded transition-colors"
                        title={locale === 'ar' ? 'مسح المسار' : 'Clear path'}
                      >
                        <span className="material-icons-outlined text-sm">clear</span>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removePath(index)}
                      className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                      title={locale === 'ar' ? 'حذف المسار' : 'Delete path'}
                    >
                      <span className="material-icons-outlined text-sm">delete</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex gap-3">
          <span className="material-icons-outlined text-blue-600 dark:text-blue-400">
            info
          </span>
          <div className="text-sm text-blue-800 dark:text-blue-300">
            <p className="font-medium mb-1">
              {locale === 'ar' ? 'كيفية استخدام الخريطة:' : 'How to use the map:'}
            </p>
            <ul className="list-disc list-inside space-y-1 text-blue-700 dark:text-blue-400">
              <li>{locale === 'ar' ? 'انقر على "إضافة مسار" لإنشاء مسار جديد' : 'Click "Add Path" to create a new path'}</li>
              <li>{locale === 'ar' ? 'حدد مساراً من القائمة لتفعيله' : 'Select a path from the list to activate it'}</li>
              <li>{locale === 'ar' ? 'انقر على الخريطة لإضافة نقاط إلى المسار النشط' : 'Click on the map to add points to the active path'}</li>
              <li>{locale === 'ar' ? 'يمكنك إضافة عدة مسارات مختلفة' : 'You can add multiple different paths'}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
